// @refresh reset
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { STUDIO_TABS } from "../../../../constants/clientViewStudio";
import { buildClientViewStudioPath } from "../../../../utils/clientViewsRoutes";
import { useClientViewIamApi } from "../../../../contexts/ClientViewIamApiContext";
import { formatDateTime } from "../../admin/users/adminUserFormatters";

const SUB_TABS = [
  { id: "users", label: "Users" },
  { id: "organizations", label: "Organizations" },
  { id: "roles", label: "Roles" },
  { id: "audit", label: "Audit log" },
];

const PORTAL_SUB_TABS = SUB_TABS.filter((tab) => tab.id !== "organizations");

const ORG_ROLE_OPTIONS = [
  { value: "CLIENT_OWNER", label: "Owner" },
  { value: "CLIENT_MANAGER", label: "Manager" },
  { value: "CLIENT_CONTRIBUTOR", label: "Contributor" },
  { value: "CLIENT_VIEWER", label: "Viewer" },
];

const AUDIT_LABELS = {
  "iam.user.invited": "User invited",
  "iam.user.status_changed": "User status changed",
  "iam.role.created": "Role created",
  "iam.role.updated": "Role updated",
  "iam.role.deleted": "Role deleted",
  "iam.org.assigned": "Organization assigned",
  "iam.org.removed": "Organization removed",
  "iam.assignment.updated": "Assignment updated",
};

function userDisplayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return name || user?.email || "User";
}

function userInitials(user) {
  const name = userDisplayName(user);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function formatOrgRole(code) {
  return ORG_ROLE_OPTIONS.find((r) => r.value === code)?.label || code || "—";
}

function formatAuditEvent(type) {
  return AUDIT_LABELS[type] || type?.replace(/\./g, " › ") || "Event";
}

function IamAvatar({ user }) {
  return (
    <span className="cv-iam__avatar" aria-hidden>
      {userInitials(user)}
    </span>
  );
}

function IamEmpty({ icon, children, action }) {
  return (
    <div className="cv-iam__empty">
      <span className="cv-iam__empty-icon">{icon}</span>
      <p className="cv-iam__empty-text">{children}</p>
      {action || null}
    </div>
  );
}

function IamLoading({ children = "Loading…" }) {
  return <div className="cv-iam__loading">{children}</div>;
}

function IamAlert({ message, tone = "error", onClose }) {
  if (!message) return null;
  return (
    <div className={`cv-iam__alert cv-iam__alert--${tone}`}>
      <span>{message}</span>
      {onClose ? (
        <button type="button" className="cv-iam__alert-close" onClick={onClose} aria-label="Dismiss">
          ×
        </button>
      ) : null}
    </div>
  );
}

function IamSectionHeader({ title, description, action }) {
  return (
    <div className="cv-iam__section-header">
      <div>
        <h3 className="cv-iam__section-title">{title}</h3>
        {description ? <p className="cv-iam__section-desc">{description}</p> : null}
      </div>
      {action || null}
    </div>
  );
}

function TempPasswordReveal({ email, password, onCopied }) {
  const [copied, setCopied] = useState(false);

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      onCopied?.();
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="cv-iam__temp-reveal">
      <p>
        Share this password with <strong>{email}</strong>. It is shown once. They must change it
        after signing in.
      </p>
      <div className="cv-iam__temp-reveal-row">
        <code>{password}</code>
        <button type="button" className="cv-iam__btn cv-iam__btn--primary cv-iam__btn--sm" onClick={copyPassword}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function IamModal({ title, subtitle, onClose, children, wide = false }) {
  return (
    <div className="cv-iam__modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className={`cv-iam__modal${wide ? " cv-iam__modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cv-iam__modal-header">
          <div>
            {subtitle ? <p className="cv-iam__modal-eyebrow">{subtitle}</p> : null}
            <h2 className="cv-iam__modal-title">{title}</h2>
          </div>
          <button type="button" className="cv-iam__btn cv-iam__btn--sm" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="cv-iam__modal-body">{children}</div>
      </div>
    </div>
  );
}

function IamField({ label, hint, children, wide = false }) {
  return (
    <label className={`cv-iam__field${wide ? " cv-iam__field--wide" : ""}`}>
      <span className="cv-iam__field-label">{label}</span>
      {children}
      {hint ? <span className="cv-iam__field-hint">{hint}</span> : null}
    </label>
  );
}

function IamStatusBadge({ active, label }) {
  return (
    <span className={`cv-iam__badge${active ? " cv-iam__badge--active" : " cv-iam__badge--inactive"}`}>
      {label}
    </span>
  );
}

function PermissionPicker({ groups, selected, onChange, disabled = false }) {
  return (
    <div className="cv-iam__perm-picker">
      {groups.map((group) => (
        <div key={group.category} className="cv-iam__perm-group">
          <span className="cv-iam__perm-cat">{group.category}</span>
          <div className="cv-iam__perm-chips">
            {group.permissions.map((permission) => {
              const on = selected.includes(permission.permissionCode);
              return (
                <button
                  key={permission.permissionCode}
                  type="button"
                  disabled={disabled}
                  className={`cv-iam__perm-chip${on ? " cv-iam__perm-chip--on" : ""}`}
                  onClick={() => {
                    if (disabled) return;
                    if (on) {
                      onChange(selected.filter((code) => code !== permission.permissionCode));
                    } else {
                      onChange([...selected, permission.permissionCode]);
                    }
                  }}
                  title={permission.permissionCode}
                >
                  {permission.permissionName}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersPane({
  clientViewId,
  canManage,
  summary,
  onRefresh,
  showPlatformLinks = true,
  lockInviteOrganization = false,
  firmMode = false,
}) {
  const {
    listClientViewIamUsers,
    inviteClientViewIamUser,
    patchClientViewIamUserStatus,
    updateClientViewIamAssignment,
    resetClientViewIamUserPassword,
    setClientViewIamUserTemporaryPassword,
    getClientViewIamUserLoginHistory,
    updateClientViewIamUserRole,
  } = useClientViewIamApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [tempPasswordLoading, setTempPasswordLoading] = useState(false);
  const [tempPasswordForm, setTempPasswordForm] = useState({ password: "", confirm: "" });
  const [revealedPassword, setRevealedPassword] = useState("");
  const [inviteReveal, setInviteReveal] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    clientOrgId: "",
    email: "",
    firstName: "",
    lastName: "",
    orgRole: firmMode ? "CLIENT_CONTRIBUTOR" : "CLIENT_VIEWER",
    sendInvitation: true,
    setTemporaryPassword: false,
  });

  const organizations = summary?.organizations || [];
  const defaultOrgId = organizations[0]?.clientOrgId || "";

  useEffect(() => {
    if (lockInviteOrganization && defaultOrgId) {
      setInviteForm((prev) =>
        prev.clientOrgId === defaultOrgId ? prev : { ...prev, clientOrgId: defaultOrgId }
      );
    }
  }, [defaultOrgId, lockInviteOrganization]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listClientViewIamUsers(clientViewId, { search, pageSize: 100 });
      setUsers(body.users || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load IAM users.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleInvite = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const body = await inviteClientViewIamUser(clientViewId, {
        ...inviteForm,
        clientOrgId: inviteForm.clientOrgId || defaultOrgId || undefined,
      });
      setInviteForm({
        clientOrgId: organizations[0]?.clientOrgId || defaultOrgId || "",
        email: "",
        firstName: "",
        lastName: "",
        orgRole: firmMode ? "CLIENT_CONTRIBUTOR" : "CLIENT_VIEWER",
        sendInvitation: true,
        setTemporaryPassword: false,
      });
      await loadUsers();
      onRefresh();
      if (body.temporaryPassword) {
        setInviteReveal({
          email: body.user?.email || inviteForm.email,
          temporaryPassword: body.temporaryPassword,
        });
        setSuccessMessage(body.message || "Temporary password created. Copy it now — it will not be shown again.");
        return;
      }
      setInviteOpen(false);
      if (body.invitationSent && body.emailDelivered === false) {
        setErrorMessage(
          firmMode
            ? `The account was created, but the invitation email was not delivered (${body.emailError || body.emailProvider || "unknown"}). Open Settings → Notifications to inspect delivery, or set a temporary password from Manage user.`
            : `The account was created, but the invitation email was not delivered (${body.emailError || body.emailProvider || "unknown"}).`
        );
      } else if (body.invitationSent) {
        setSuccessMessage("Invitation email sent.");
      } else {
        setSuccessMessage("Account saved without sending an invitation.");
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to invite user.");
    }
  };

  const handleStatus = async (user, status) => {
    try {
      await patchClientViewIamUserStatus(clientViewId, user.clientUserId, { status });
      await loadUsers();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update user status.");
    }
  };

  const handleAssignmentRole = async (user, iamRoleId) => {
    try {
      await updateClientViewIamAssignment(clientViewId, user.assignmentId, { iamRoleId });
      await loadUsers();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update assignment role.");
    }
  };

  const openManageUser = async (user) => {
    setManageUser(user);
    setLoginHistory([]);
    setLoginHistoryLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    setTempPasswordForm({ password: "", confirm: "" });
    setRevealedPassword("");
    try {
      const body = await getClientViewIamUserLoginHistory(clientViewId, user.clientUserId, {
        pageSize: 25,
      });
      setLoginHistory(body.events || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load login history.");
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  const handleOrgRoleChange = async (user, orgRole) => {
    try {
      const body = await updateClientViewIamUserRole(clientViewId, user.clientUserId, { orgRole });
      setUsers(body.users || []);
      setManageUser((current) =>
        current?.clientUserId === user.clientUserId
          ? { ...current, orgRole }
          : current
      );
      setSuccessMessage("Organization role updated.");
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update organization role.");
    }
  };

  const handleManageViewRole = async (user, iamRoleId) => {
    try {
      await updateClientViewIamAssignment(clientViewId, user.assignmentId, { iamRoleId });
      await loadUsers();
      setManageUser((current) =>
        current?.clientUserId === user.clientUserId
          ? {
              ...current,
              iamRoleId: iamRoleId || null,
              iamRoleName:
                (summary?.roles || []).find((role) => role.iamRoleId === iamRoleId)?.roleName ||
                "Default",
            }
          : current
      );
      setSuccessMessage("View role updated.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to update view role.");
    }
  };

  const handleResetPassword = async (user) => {
    if (
      !window.confirm(
        `Send a password reset email to ${user.email}? Active sessions will be revoked when they set a new password.`
      )
    ) {
      return;
    }
    setResetLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const body = await resetClientViewIamUserPassword(clientViewId, user.clientUserId, {
        sendEmail: true,
      });
      if (body.emailDelivered === false) {
        setErrorMessage(
          body.message ||
            "Password reset was created but the email was not delivered. Check Settings → Notifications."
        );
      } else {
        setSuccessMessage(
          body.devToken
            ? `${body.message || "Password reset email sent."} Dev token: ${body.devToken}`
            : body.message || "Password reset email sent."
        );
      }
      const historyBody = await getClientViewIamUserLoginHistory(clientViewId, user.clientUserId, {
        pageSize: 25,
      });
      setLoginHistory(historyBody.events || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to send password reset.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleSetTemporaryPassword = async (user, { generate } = {}) => {
    if (!generate && tempPasswordForm.password !== tempPasswordForm.confirm) {
      setErrorMessage("Temporary passwords do not match.");
      return;
    }
    if (
      !window.confirm(
        `Set a temporary password for ${user.email}? ${
          user.userStatus === "INVITED" ? "This will activate the account. " : ""
        }They must change it after signing in.`
      )
    ) {
      return;
    }
    setTempPasswordLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const body = await setClientViewIamUserTemporaryPassword(clientViewId, user.clientUserId, {
        password: generate ? undefined : tempPasswordForm.password,
      });
      setRevealedPassword(body.temporaryPassword || "");
      setTempPasswordForm({ password: "", confirm: "" });
      setSuccessMessage(body.message || "Temporary password set. Copy it now — it will not be shown again.");
      await loadUsers();
      const historyBody = await getClientViewIamUserLoginHistory(clientViewId, user.clientUserId, {
        pageSize: 25,
      });
      setLoginHistory(historyBody.events || []);
      setManageUser((current) =>
        current?.clientUserId === user.clientUserId
          ? { ...current, userStatus: body.activated ? "ACTIVE" : current.userStatus }
          : current
      );
    } catch (error) {
      setErrorMessage(error.message || "Unable to set a temporary password.");
    } finally {
      setTempPasswordLoading(false);
    }
  };

  return (
    <div className="cv-iam__pane">
      <IamSectionHeader
        title={firmMode ? "Employees" : "Portal users"}
        description={
          firmMode
            ? "Invite teammates to the Midpoint Media administrative portal. They join the Midpoint Media organization automatically."
            : "People who can sign in to this Client View through their organization."
        }
        action={
          canManage ? (
            <button
              type="button"
              className="cv-iam__btn cv-iam__btn--primary"
              onClick={() => setInviteOpen(true)}
            >
              {firmMode ? "+ Invite teammate" : "+ Invite user"}
            </button>
          ) : null
        }
      />

      <div className="cv-iam__toolbar">
        <div className="cv-iam__search-wrap">
          <span className="cv-iam__search-icon" aria-hidden>
            ⌕
          </span>
          <input
            type="search"
            className="cv-iam__search"
            placeholder={firmMode ? "Search by name or email…" : "Search by name, email, or organization…"}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {showPlatformLinks ? (
          <Link className="cv-iam__link" to="/admin/client-organizations">
            Manage all organizations →
          </Link>
        ) : null}
      </div>

      <IamAlert message={errorMessage} onClose={() => setErrorMessage("")} />
      <IamAlert
        message={successMessage}
        tone="success"
        onClose={() => setSuccessMessage("")}
      />

      {loading ? (
        <IamLoading>Loading users…</IamLoading>
      ) : users.length ? (
        <div className="cv-iam__card-list">
          {users.map((user) => (
            <article key={`${user.clientUserId}-${user.assignmentId}`} className="cv-iam__user-card">
              <div className="cv-iam__user-top">
                <IamAvatar user={user} />
                <div className="cv-iam__user-info">
                  <span className="cv-iam__user-name">{userDisplayName(user)}</span>
                  <span className="cv-iam__user-email">{user.email}</span>
                </div>
                <IamStatusBadge
                  active={user.userStatus === "ACTIVE"}
                  label={user.userStatus === "ACTIVE" ? "Active" : user.userStatus || "Inactive"}
                />
              </div>
              <div className="cv-iam__user-meta">
                {firmMode ? null : (
                  <div className="cv-iam__meta-item">
                    <span className="cv-iam__meta-label">Organization</span>
                    <span className="cv-iam__meta-value">{user.organizationName}</span>
                  </div>
                )}
                <div className="cv-iam__meta-item">
                  <span className="cv-iam__meta-label">{firmMode ? "Role" : "Org role"}</span>
                  <span className="cv-iam__meta-value">{formatOrgRole(user.orgRole)}</span>
                </div>
                {firmMode ? null : (
                  <div className="cv-iam__meta-item">
                    <span className="cv-iam__meta-label">View role</span>
                    {canManage ? (
                      <select
                        className="cv-iam__field-select cv-iam__field-select--compact"
                        value={user.iamRoleId || ""}
                        onChange={(event) =>
                          handleAssignmentRole(user, event.target.value || null)
                        }
                      >
                        <option value="">Default (legacy)</option>
                        {(summary?.roles || []).map((role) => (
                          <option key={role.iamRoleId} value={role.iamRoleId}>
                            {role.roleName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="cv-iam__meta-value">{user.iamRoleName || "Default"}</span>
                    )}
                  </div>
                )}
                <div className="cv-iam__meta-item">
                  <span className="cv-iam__meta-label">Last login</span>
                  <span className="cv-iam__meta-value">
                    {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : "Never"}
                  </span>
                </div>
              </div>
              {canManage ? (
                <div className="cv-iam__card-actions">
                  <button
                    type="button"
                    className="cv-iam__btn cv-iam__btn--sm"
                    onClick={() => openManageUser(user)}
                  >
                    Manage user
                  </button>
                  {user.userStatus === "ACTIVE" ? (
                    <button
                      type="button"
                      className="cv-iam__btn cv-iam__btn--sm cv-iam__btn--danger"
                      onClick={() => handleStatus(user, "DISABLED")}
                    >
                      Disable access
                    </button>
                  ) : user.userStatus === "INVITED" ? null : (
                    <button
                      type="button"
                      className="cv-iam__btn cv-iam__btn--sm cv-iam__btn--primary"
                      onClick={() => handleStatus(user, "ACTIVE")}
                    >
                      Restore access
                    </button>
                  )}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <IamEmpty icon="👥" action={canManage ? (
          <button type="button" className="cv-iam__btn cv-iam__btn--primary" onClick={() => setInviteOpen(true)}>
            {firmMode ? "Invite teammate" : "Invite first user"}
          </button>
        ) : null}>
          {firmMode
            ? "No employee accounts yet. Invite someone to Midpoint Media."
            : "No portal users yet. Assign an organization to this view, then invite users here."}
        </IamEmpty>
      )}

      {inviteOpen ? (
        <IamModal
          title={firmMode ? "Invite teammate" : "Invite portal user"}
          subtitle={firmMode ? "Midpoint Media" : "IAM"}
          onClose={() => {
            setInviteOpen(false);
            setInviteReveal(null);
          }}
        >
          {inviteReveal ? (
            <>
              <TempPasswordReveal
                email={inviteReveal.email}
                password={inviteReveal.temporaryPassword}
              />
              <div className="cv-iam__form-actions">
                <button
                  type="button"
                  className="cv-iam__btn cv-iam__btn--primary"
                  onClick={() => {
                    setInviteOpen(false);
                    setInviteReveal(null);
                  }}
                >
                  Done
                </button>
              </div>
            </>
          ) : (
          <form className="cv-iam__form" onSubmit={handleInvite}>
            {!lockInviteOrganization ? (
              <IamField label="Organization">
                <select
                  className="cv-iam__field-select"
                  required
                  value={inviteForm.clientOrgId}
                  onChange={(event) =>
                    setInviteForm((prev) => ({ ...prev, clientOrgId: event.target.value }))
                  }
                >
                  <option value="">Select organization</option>
                  {organizations.map((org) => (
                    <option key={org.clientOrgId} value={org.clientOrgId}>
                      {org.organizationName}
                    </option>
                  ))}
                </select>
              </IamField>
            ) : (
              <p className="cv-iam__field-hint">
                Inviting to{" "}
                <strong>
                  {organizations[0]?.organizationName || (firmMode ? "Midpoint Media" : "this organization")}
                </strong>
              </p>
            )}
            <IamField label="Email address">
              <input
                type="email"
                className="cv-iam__field-input"
                required
                value={inviteForm.email}
                onChange={(event) =>
                  setInviteForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />
            </IamField>
            <div className="cv-iam__form-grid">
              <IamField label="First name">
                <input
                  className="cv-iam__field-input"
                  value={inviteForm.firstName}
                  onChange={(event) =>
                    setInviteForm((prev) => ({ ...prev, firstName: event.target.value }))
                  }
                />
              </IamField>
              <IamField label="Last name">
                <input
                  className="cv-iam__field-input"
                  value={inviteForm.lastName}
                  onChange={(event) =>
                    setInviteForm((prev) => ({ ...prev, lastName: event.target.value }))
                  }
                />
              </IamField>
            </div>
            <IamField
              label={firmMode ? "Portal role" : "Organization role"}
              hint={
                firmMode
                  ? "What they can do in the Midpoint Media administrative portal."
                  : "Controls baseline permissions within the client org."
              }
            >
              <select
                className="cv-iam__field-select"
                value={inviteForm.orgRole}
                onChange={(event) =>
                  setInviteForm((prev) => ({ ...prev, orgRole: event.target.value }))
                }
              >
                {ORG_ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </IamField>
            <label className="cv-iam__checkbox">
              <input
                type="checkbox"
                checked={inviteForm.sendInvitation}
                disabled={inviteForm.setTemporaryPassword}
                onChange={(event) =>
                  setInviteForm((prev) => ({ ...prev, sendInvitation: event.target.checked }))
                }
              />
              <span>Send invitation email</span>
            </label>
            <label className="cv-iam__checkbox">
              <input
                type="checkbox"
                checked={inviteForm.setTemporaryPassword}
                onChange={(event) =>
                  setInviteForm((prev) => ({
                    ...prev,
                    setTemporaryPassword: event.target.checked,
                    sendInvitation: event.target.checked ? false : prev.sendInvitation,
                  }))
                }
              />
              <span>Set a temporary password so they can sign in without email</span>
            </label>
            <div className="cv-iam__form-actions">
              <button type="submit" className="cv-iam__btn cv-iam__btn--primary">
                {inviteForm.setTemporaryPassword ? "Create account" : "Send invitation"}
              </button>
              <button type="button" className="cv-iam__btn" onClick={() => setInviteOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
          )}
        </IamModal>
      ) : null}

      {manageUser ? (
        <IamModal
          title={userDisplayName(manageUser)}
          subtitle={firmMode ? "Employee account" : "Manage portal user"}
          wide
          onClose={() => setManageUser(null)}
        >
          <div className="cv-iam__user-manage">
            <p className="cv-iam__user-manage-email">{manageUser.email}</p>

            <div className="cv-iam__form-grid">
              <IamField
                label={firmMode ? "Portal role" : "Organization role"}
                hint={
                  firmMode
                    ? "What this person can do in the Midpoint Media portal."
                    : "Baseline permissions within the client org."
                }
              >
                <select
                  className="cv-iam__field-select"
                  value={manageUser.orgRole || ""}
                  onChange={(event) => handleOrgRoleChange(manageUser, event.target.value)}
                >
                  {ORG_ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </IamField>
              {firmMode ? null : (
                <IamField label="View role" hint="Permissions for this Client View.">
                  <select
                    className="cv-iam__field-select"
                    value={manageUser.iamRoleId || ""}
                    onChange={(event) =>
                      handleManageViewRole(manageUser, event.target.value || null)
                    }
                  >
                    <option value="">Default (legacy)</option>
                    {(summary?.roles || []).map((role) => (
                      <option key={role.iamRoleId} value={role.iamRoleId}>
                        {role.roleName}
                      </option>
                    ))}
                  </select>
                </IamField>
              )}
            </div>

            <div className="cv-iam__user-manage-actions">
              <button
                type="button"
                className="cv-iam__btn cv-iam__btn--primary"
                disabled={resetLoading}
                onClick={() => handleResetPassword(manageUser)}
              >
                {resetLoading ? "Sending…" : "Send password reset email"}
              </button>
            </div>

            <section className="cv-iam__temp-password">
              <h3 className="cv-iam__section-title">Temporary password</h3>
              <p className="cv-iam__section-desc">
                Use this when email delivery fails or you need to hand someone a sign-in password.
                {manageUser.userStatus === "INVITED"
                  ? " Setting one activates the account immediately."
                  : ""}{" "}
                They will be asked to change it after signing in.
              </p>
              {revealedPassword ? (
                <TempPasswordReveal email={manageUser.email} password={revealedPassword} />
              ) : (
                <div className="cv-iam__form-grid">
                  <IamField label="Custom password" hint="Leave blank and generate one instead.">
                    <input
                      type="password"
                      className="cv-iam__field-input"
                      autoComplete="new-password"
                      value={tempPasswordForm.password}
                      onChange={(event) =>
                        setTempPasswordForm((prev) => ({ ...prev, password: event.target.value }))
                      }
                    />
                  </IamField>
                  <IamField label="Confirm password">
                    <input
                      type="password"
                      className="cv-iam__field-input"
                      autoComplete="new-password"
                      value={tempPasswordForm.confirm}
                      onChange={(event) =>
                        setTempPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))
                      }
                    />
                  </IamField>
                </div>
              )}
              <div className="cv-iam__user-manage-actions">
                <button
                  type="button"
                  className="cv-iam__btn cv-iam__btn--primary"
                  disabled={tempPasswordLoading}
                  onClick={() => handleSetTemporaryPassword(manageUser, { generate: true })}
                >
                  {tempPasswordLoading ? "Setting…" : "Generate temporary password"}
                </button>
                <button
                  type="button"
                  className="cv-iam__btn"
                  disabled={tempPasswordLoading || !tempPasswordForm.password}
                  onClick={() => handleSetTemporaryPassword(manageUser, { generate: false })}
                >
                  Set custom password
                </button>
              </div>
            </section>

            <section className="cv-iam__login-history">
              <h3 className="cv-iam__section-title">Login history</h3>
              <p className="cv-iam__section-desc">
                Recent sign-ins, sign-outs, and password events for this user.
              </p>
              {loginHistoryLoading ? (
                <IamLoading>Loading login history…</IamLoading>
              ) : loginHistory.length ? (
                <div className="cv-iam__timeline cv-iam__timeline--compact">
                  {loginHistory.map((entry) => (
                    <article key={entry.eventId} className="cv-iam__timeline-item">
                      <div className="cv-iam__timeline-dot" aria-hidden />
                      <div className="cv-iam__timeline-body">
                        <div className="cv-iam__timeline-top">
                          <span className="cv-iam__timeline-event">{entry.label}</span>
                          <time className="cv-iam__timeline-time">
                            {formatDateTime(entry.createdAt)}
                          </time>
                        </div>
                        <p className="cv-iam__timeline-meta">
                          {entry.deviceLabel}
                          {entry.ipAddress ? ` · ${entry.ipAddress}` : ""}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="cv-iam__empty-text">No login events recorded yet.</p>
              )}
            </section>
          </div>
        </IamModal>
      ) : null}
    </div>
  );
}

function OrganizationsPane({ clientViewId, canManage, summary, onRefresh }) {
  const {
    getClientViewIamSummary,
    listClientViewIamAvailableOrganizations,
    listClientViewIamRoles,
    assignOrganizationToClientView,
    removeOrganizationFromClientView,
    updateClientViewIamAssignment,
  } = useClientViewIamApi();
  const [organizations, setOrganizations] = useState(summary?.organizations || []);
  const [available, setAvailable] = useState([]);
  const [roles, setRoles] = useState(summary?.roles || []);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    clientOrgId: "",
    role: "CLIENT_MANAGER",
    iamRoleId: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [summaryBody, availableBody, rolesBody] = await Promise.all([
        getClientViewIamSummary(clientViewId),
        listClientViewIamAvailableOrganizations(clientViewId),
        listClientViewIamRoles(clientViewId),
      ]);
      setOrganizations(summaryBody.organizations || []);
      setAvailable(availableBody.organizations || []);
      setRoles(rolesBody.roles || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load organizations.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAssign = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    try {
      await assignOrganizationToClientView(clientViewId, {
        clientOrgId: assignForm.clientOrgId,
        role: assignForm.role,
        iamRoleId: assignForm.iamRoleId || undefined,
      });
      setAssignOpen(false);
      await load();
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message || "Unable to assign organization.");
    }
  };

  const handleRemove = async (assignmentId) => {
    if (!window.confirm("Remove this organization from the Client View?")) return;
    try {
      await removeOrganizationFromClientView(clientViewId, assignmentId);
      await load();
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message || "Unable to remove organization.");
    }
  };

  const handleRoleChange = async (org, iamRoleId) => {
    try {
      await updateClientViewIamAssignment(clientViewId, org.assignmentId, { iamRoleId });
      await load();
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update organization role.");
    }
  };

  return (
    <div className="cv-iam__pane">
      <IamSectionHeader
        title="Organizations"
        description="Client orgs with access to this view. Their users inherit portal access."
        action={
          canManage ? (
            <button
              type="button"
              className="cv-iam__btn cv-iam__btn--primary"
              onClick={() => setAssignOpen(true)}
            >
              + Assign organization
            </button>
          ) : null
        }
      />

      <IamAlert message={errorMessage} onClose={() => setErrorMessage("")} />
      {loading ? <IamLoading>Loading organizations…</IamLoading> : null}

      {!loading && organizations.length ? (
        <div className="cv-iam__org-grid">
          {organizations.map((org) => (
            <article key={org.assignmentId} className="cv-iam__org-card">
              <div className="cv-iam__org-top">
                <span className="cv-iam__org-icon" aria-hidden>
                  🏢
                </span>
                <div className="cv-iam__org-info">
                  <span className="cv-iam__org-name">{org.organizationName}</span>
                  <span className="cv-iam__org-id">{org.clientOrgId}</span>
                </div>
                <IamStatusBadge active={org.status === "ACTIVE"} label={org.status || "—"} />
              </div>
              <div className="cv-iam__org-fields">
                <div className="cv-iam__meta-item">
                  <span className="cv-iam__meta-label">Assignment role</span>
                  <span className="cv-iam__meta-value">{formatOrgRole(org.assignmentRole)}</span>
                </div>
                <div className="cv-iam__meta-item">
                  <span className="cv-iam__meta-label">View IAM role</span>
                  {canManage ? (
                    <select
                      className="cv-iam__field-select cv-iam__field-select--compact"
                      value={org.iamRoleId || ""}
                      onChange={(event) => handleRoleChange(org, event.target.value || null)}
                    >
                      <option value="">System default</option>
                      {roles.map((role) => (
                        <option key={role.iamRoleId} value={role.iamRoleId}>
                          {role.roleName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="cv-iam__meta-value">{org.iamRoleName || "System default"}</span>
                  )}
                </div>
              </div>
              {canManage ? (
                <div className="cv-iam__card-actions">
                  <button
                    type="button"
                    className="cv-iam__btn cv-iam__btn--sm cv-iam__btn--danger"
                    onClick={() => handleRemove(org.assignmentId)}
                  >
                    Remove access
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}

      {!loading && !organizations.length ? (
        <IamEmpty icon="🏢" action={canManage ? (
          <button type="button" className="cv-iam__btn cv-iam__btn--primary" onClick={() => setAssignOpen(true)}>
            Assign organization
          </button>
        ) : null}>
          No organizations are linked to this Client View yet.
        </IamEmpty>
      ) : null}

      {assignOpen ? (
        <IamModal title="Assign organization" subtitle="IAM" onClose={() => setAssignOpen(false)}>
          <form className="cv-iam__form" onSubmit={handleAssign}>
            <IamField label="Organization">
              <select
                className="cv-iam__field-select"
                required
                value={assignForm.clientOrgId}
                onChange={(event) =>
                  setAssignForm((prev) => ({ ...prev, clientOrgId: event.target.value }))
                }
              >
                <option value="">Select organization</option>
                {available.map((org) => (
                  <option key={org.clientOrgId} value={org.clientOrgId}>
                    {org.organizationName}
                  </option>
                ))}
              </select>
            </IamField>
            <IamField label="Assignment role">
              <select
                className="cv-iam__field-select"
                value={assignForm.role}
                onChange={(event) =>
                  setAssignForm((prev) => ({ ...prev, role: event.target.value }))
                }
              >
                {ORG_ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </IamField>
            <IamField label="View IAM role" hint="Optional override for this organization's users.">
              <select
                className="cv-iam__field-select"
                value={assignForm.iamRoleId}
                onChange={(event) =>
                  setAssignForm((prev) => ({ ...prev, iamRoleId: event.target.value }))
                }
              >
                <option value="">Match assignment role</option>
                {roles.map((role) => (
                  <option key={role.iamRoleId} value={role.iamRoleId}>
                    {role.roleName}
                  </option>
                ))}
              </select>
            </IamField>
            <div className="cv-iam__form-actions">
              <button type="submit" className="cv-iam__btn cv-iam__btn--primary">
                Assign organization
              </button>
              <button type="button" className="cv-iam__btn" onClick={() => setAssignOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </IamModal>
      ) : null}
    </div>
  );
}

function AuditPane({ clientViewId }) {
  const { listClientViewIamAuditLog } = useClientViewIamApi();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listClientViewIamAuditLog(clientViewId, { pageSize: 100 })
      .then((body) => {
        if (!cancelled) setEntries(body.entries || []);
      })
      .catch((error) => {
        if (!cancelled) setErrorMessage(error.message || "Unable to load audit log.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId]);

  return (
    <div className="cv-iam__pane">
      <IamSectionHeader
        title="Audit log"
        description="Invites, role changes, and organization assignments for this view."
      />

      <IamAlert message={errorMessage} onClose={() => setErrorMessage("")} />

      {loading ? (
        <IamLoading>Loading audit log…</IamLoading>
      ) : entries.length ? (
        <div className="cv-iam__timeline">
          {entries.map((entry) => (
            <article key={entry.auditLogId} className="cv-iam__timeline-item">
              <div className="cv-iam__timeline-dot" aria-hidden />
              <div className="cv-iam__timeline-body">
                <div className="cv-iam__timeline-top">
                  <span className="cv-iam__timeline-event">{formatAuditEvent(entry.eventType)}</span>
                  <time className="cv-iam__timeline-time">{formatDateTime(entry.createdAt)}</time>
                </div>
                {(entry.entityType || entry.entityId) && (
                  <p className="cv-iam__timeline-meta">
                    {entry.entityType || "Entity"}
                    {entry.entityId ? ` · ${entry.entityId}` : ""}
                  </p>
                )}
                {entry.metadata ? (
                  <pre className="cv-iam__timeline-detail">
                    {JSON.stringify(entry.metadata, null, 2)}
                  </pre>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <IamEmpty icon="📋">No IAM events recorded for this Client View yet.</IamEmpty>
      )}
    </div>
  );
}

function RolesPane({ clientViewId, canManage, onRefresh }) {
  const {
    listClientViewIamRoles,
    listClientViewIamPermissions,
    createClientViewIamRole,
    updateClientViewIamRole,
    deleteClientViewIamRole,
  } = useClientViewIamApi();
  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [rolesBody, permissionsBody] = await Promise.all([
        listClientViewIamRoles(clientViewId),
        listClientViewIamPermissions(clientViewId),
      ]);
      setRoles(rolesBody.roles || []);
      setPermissionGroups(permissionsBody.groups || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load IAM roles.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditor({
      mode: "create",
      roleCode: "",
      roleName: "",
      description: "",
      permissionCodes: [],
    });
  };

  const openEdit = (role) => {
    setEditor({
      mode: "edit",
      iamRoleId: role.iamRoleId,
      roleCode: role.roleCode,
      roleName: role.roleName,
      description: role.description || "",
      permissionCodes: [...(role.permissionCodes || [])],
      isSystem: role.isSystem,
    });
  };

  const saveEditor = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    try {
      if (editor.mode === "create") {
        await createClientViewIamRole(clientViewId, {
          roleCode: editor.roleCode,
          roleName: editor.roleName,
          description: editor.description,
          permissionCodes: editor.permissionCodes,
        });
      } else {
        await updateClientViewIamRole(clientViewId, editor.iamRoleId, {
          roleName: editor.roleName,
          description: editor.description,
          permissionCodes: editor.permissionCodes,
        });
      }
      setEditor(null);
      await load();
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message || "Unable to save role.");
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.roleName}"?`)) return;
    try {
      await deleteClientViewIamRole(clientViewId, role.iamRoleId);
      await load();
      onRefresh();
    } catch (error) {
      setErrorMessage(error.message || "Unable to delete role.");
    }
  };

  return (
    <div className="cv-iam__pane">
      <IamSectionHeader
        title="Roles & permissions"
        description="Define what portal users can do in this Client View."
        action={
          canManage ? (
            <button type="button" className="cv-iam__btn cv-iam__btn--primary" onClick={openCreate}>
              + New role
            </button>
          ) : null
        }
      />

      <IamAlert message={errorMessage} onClose={() => setErrorMessage("")} />

      {loading ? (
        <IamLoading>Loading roles…</IamLoading>
      ) : (
        <div className="cv-iam__role-grid">
          {roles.map((role) => (
            <article
              key={role.iamRoleId}
              className={`cv-iam__role-card${role.isSystem ? " cv-iam__role-card--system" : ""}`}
            >
              <div className="cv-iam__role-top">
                <span className="cv-iam__role-icon" aria-hidden>
                  {role.isSystem ? "🔒" : "🛡️"}
                </span>
                <div className="cv-iam__role-info">
                  <span className="cv-iam__role-name">{role.roleName}</span>
                  <code className="cv-iam__role-code">{role.roleCode}</code>
                </div>
                <div className="cv-iam__role-tags">
                  {role.isSystem ? <span className="cv-iam__tag">System</span> : null}
                  {!role.isActive ? <span className="cv-iam__tag cv-iam__tag--warn">Inactive</span> : null}
                </div>
              </div>
              <p className="cv-iam__role-desc">{role.description || "No description provided."}</p>
              <span className="cv-iam__role-count">
                {(role.permissionCodes || []).length} permission
                {(role.permissionCodes || []).length === 1 ? "" : "s"}
              </span>
              {canManage ? (
                <div className="cv-iam__card-actions">
                  <button type="button" className="cv-iam__btn cv-iam__btn--sm" onClick={() => openEdit(role)}>
                    Edit
                  </button>
                  {!role.isSystem ? (
                    <button
                      type="button"
                      className="cv-iam__btn cv-iam__btn--sm cv-iam__btn--danger"
                      onClick={() => handleDelete(role)}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {editor ? (
        <IamModal
          title={editor.mode === "create" ? "Create role" : "Edit role"}
          subtitle="IAM role"
          wide
          onClose={() => setEditor(null)}
        >
          <form className="cv-iam__form" onSubmit={saveEditor}>
            {editor.mode === "create" ? (
              <IamField label="Role code" hint="Uppercase identifier, e.g. CONTENT_EDITOR">
                <input
                  className="cv-iam__field-input"
                  required
                  value={editor.roleCode}
                  onChange={(event) =>
                    setEditor((prev) => ({ ...prev, roleCode: event.target.value.toUpperCase() }))
                  }
                  placeholder="CUSTOM_EDITOR"
                />
              </IamField>
            ) : (
              <p className="cv-iam__code-readout">
                Code: <code>{editor.roleCode}</code>
              </p>
            )}
            <IamField label="Display name">
              <input
                className="cv-iam__field-input"
                required
                value={editor.roleName}
                onChange={(event) =>
                  setEditor((prev) => ({ ...prev, roleName: event.target.value }))
                }
              />
            </IamField>
            <IamField label="Description" wide>
              <textarea
                className="cv-iam__field-input cv-iam__field-textarea"
                rows={3}
                value={editor.description}
                onChange={(event) =>
                  setEditor((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </IamField>
            <IamField label="Permissions" wide hint={editor.isSystem ? "System roles cannot be edited." : null}>
              <PermissionPicker
                groups={permissionGroups}
                selected={editor.permissionCodes}
                onChange={(permissionCodes) =>
                  setEditor((prev) => ({ ...prev, permissionCodes }))
                }
                disabled={editor.isSystem}
              />
            </IamField>
            <div className="cv-iam__form-actions">
              <button
                type="submit"
                className="cv-iam__btn cv-iam__btn--primary"
                disabled={editor.isSystem}
              >
                Save role
              </button>
              <button type="button" className="cv-iam__btn" onClick={() => setEditor(null)}>
                Cancel
              </button>
            </div>
          </form>
        </IamModal>
      ) : null}
    </div>
  );
}

const StudioIamSection = ({ clientViewId, canManage, variant = "studio", firmMode: firmModeProp = false }) => {
  const {
    getClientViewIamSummary,
    listClientViewIamRoles,
  } = useClientViewIamApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const subTabs = firmModeProp
    ? PORTAL_SUB_TABS.filter((tab) => tab.id !== "roles")
    : variant === "portal"
      ? PORTAL_SUB_TABS
      : SUB_TABS;
  const validSections = new Set(subTabs.map((tab) => tab.id));
  const syncStudioIamTab = variant === "studio";

  const sectionParam = searchParams.get("section");
  const activeSubTab = validSections.has(sectionParam) ? sectionParam : "users";

  const setActiveSubTab = useCallback(
    (sectionId) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (syncStudioIamTab) {
            next.set("tab", STUDIO_TABS.IAM);
          }
          if (sectionId === "users") {
            next.delete("section");
          } else {
            next.set("section", sectionId);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, syncStudioIamTab]
  );

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const body = await getClientViewIamSummary(clientViewId);
      const rolesBody = await listClientViewIamRoles(clientViewId);
      setSummary({ ...body, roles: rolesBody.roles || [] });
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const stats = summary?.summary;
  const firmMode = Boolean(firmModeProp || summary?.viewSlug === "midpoint-media");
  const portalHost = firmMode
    ? "midpointmedia.com/v/midpoint-media/admin"
    : summary?.viewSlug
      ? `${summary.viewSlug}.midpointmedia.com`
      : null;
  const portalLoginPath = firmMode
    ? "/v/midpoint-media/admin/login"
    : summary?.viewSlug
      ? `/v/${summary.viewSlug}/partner/login`
      : null;

  return (
    <section className="cv-iam">
      <div className="cv-iam__top">
        <div className="cv-iam__top-main">
          {portalHost && variant !== "platform" ? (
            <div className="cv-iam__portal">
              <span className="cv-iam__portal-label">Portal</span>
              <code className="cv-iam__portal-host">{portalHost}</code>
              {portalLoginPath ? (
                <a
                  className="cv-iam__portal-link"
                  href={portalLoginPath}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open sign-in
                </a>
              ) : null}
            </div>
          ) : null}

          {loading ? (
            <p className="cv-iam__top-loading">Loading access summary…</p>
          ) : stats ? (
            <div className="cv-iam__stats" aria-label="IAM summary">
              <span className="cv-iam__stat">
                <strong>{stats.userCount ?? 0}</strong> {firmMode ? "employees" : "users"}
              </span>
              {firmMode ? null : (
                <>
                  <span className="cv-iam__stat-sep" aria-hidden>
                    ·
                  </span>
                  <span className="cv-iam__stat">
                    <strong>{stats.organizationCount ?? 0}</strong> organizations
                  </span>
                </>
              )}
              <span className="cv-iam__stat-sep" aria-hidden>
                ·
              </span>
              <span className="cv-iam__stat">
                <strong>{stats.roleCount ?? 0}</strong> roles
              </span>
              {(stats.customRoleCount ?? 0) > 0 ? (
                <>
                  <span className="cv-iam__stat-sep" aria-hidden>
                    ·
                  </span>
                  <span className="cv-iam__stat cv-iam__stat--muted">
                    {stats.customRoleCount} custom
                  </span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="cv-iam__top-links">
          {variant === "studio" ? (
            <>
              <Link className="cv-iam__top-link" to="/admin/client-organizations">
                Client organizations
              </Link>
              <Link
                className="cv-iam__top-link"
                to={buildClientViewStudioPath(clientViewId, STUDIO_TABS.VERSIONS)}
              >
                Studio activity
              </Link>
            </>
          ) : null}
        </div>
      </div>

      <nav className="cv-iam__tabs" role="tablist" aria-label="IAM sections">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeSubTab === tab.id}
            className={`cv-iam__tab${activeSubTab === tab.id ? " cv-iam__tab--active" : ""}`}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeSubTab === "users" ? (
        <UsersPane
          clientViewId={clientViewId}
          canManage={canManage}
          summary={summary}
          onRefresh={loadSummary}
          showPlatformLinks={variant === "studio" || variant === "platform"}
          lockInviteOrganization={variant === "portal" || firmMode}
          firmMode={firmMode}
        />
      ) : null}
      {activeSubTab === "organizations" ? (
        <OrganizationsPane
          clientViewId={clientViewId}
          canManage={canManage}
          summary={summary}
          onRefresh={loadSummary}
        />
      ) : null}
      {activeSubTab === "audit" ? <AuditPane clientViewId={clientViewId} /> : null}
      {activeSubTab === "roles" ? (
        <RolesPane clientViewId={clientViewId} canManage={canManage} onRefresh={loadSummary} />
      ) : null}
    </section>
  );
};

export default StudioIamSection;
